'use client';
import { FollowList } from '@interfaces/auth';
import { getCanonicalUrl } from '@utils/canonical-url';
import {
  getCanonicalLeagueTeamSlug,
  getLeagueTeamPath,
} from '@utils/navigation/league-public-paths';
import { getMlsTeamListName } from '@utils/navigation/mlsTeamConfig';
import { isProductionSeoEnabled } from '@utils/config/is-production-seo';
import kebabCase from '@utils/kebab';
import { JsonLd } from '@v2/components/JsonLd';

import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { TeamListLayout } from '../../constants/eventPageUiTypes';
import { getTeamsByLeague as getEnrichedTeamsByLeague } from '../../services/getTeamsByLeague';
import { getAllLeagueTeamsAuth } from './getAllLeagueTeamsAuth';
import { TeamItem } from './TeamItem';

interface Props {
  teams: any[];
  leagueName: string;
  leagueSlug: string;
  sectionTitle?: string;
  sectionIntro?: string;
  teamsGridClassName?: string;
  currentTeamSlug?: string;
  layout?: TeamListLayout;
}

export const AllLeagueTeams = ({
  teams,
  leagueName,
  leagueSlug,
  sectionTitle,
  sectionIntro,
  teamsGridClassName,
  currentTeamSlug,
  layout = 'grouped',
}: Props) => {
  const [displayTeams, setDisplayTeams] = useState(teams);
  const [favoritesList, setFavoritesList] = useState<FollowList>();
  const { data: session, update } = useSession();
  const { favorites: sessionFavorites, userToken } =
    getAllLeagueTeamsAuth(session);

  useEffect(() => {
    setFavoritesList(sessionFavorites);
  }, [sessionFavorites]);

  useEffect(() => {
    let isActive = true;
    setDisplayTeams(teams);

    getEnrichedTeamsByLeague(leagueSlug)
      .then((enrichedTeams) => {
        if (isActive && enrichedTeams.length) {
          setDisplayTeams(enrichedTeams);
        }
      })
      .catch(() => {
        // Static teams remain visible when API enrichment is unavailable.
      });

    return () => {
      isActive = false;
    };
  }, [leagueSlug, teams]);

  const onFavoriteUpdate = async (item: FollowList[number]) => {
    await update({
      follow: [
        ...(favoritesList || []),
        { id: item.id, type: 'p', name: item.name },
      ],
    });
    setFavoritesList((oldValue) => {
      return [
        ...(oldValue || []),
        { id: item.id, type: 'performer', name: item.name },
      ];
    });
  };

  const onRemoveFavoriteUpdate = async (item: FollowList[number]) => {
    const removedFollowList = favoritesList?.filter(
      (fav) => fav.id !== item.id
    );
    await update({
      follow: removedFollowList,
    });

    setFavoritesList(removedFollowList);
  };

  if (!displayTeams.length) return null;

  const flatTeams = displayTeams.flatMap((division) => division?.teams || []);
  const collator = new Intl.Collator('en', {
    sensitivity: 'base',
  });

  const getTeamPath = (team: {
    team: string;
    league: string;
    slug?: string;
    canonicalSlug?: string;
  }) => {
    const urlSlug = leagueSlug?.toLowerCase();
    if (!urlSlug) return '';

    if (team.canonicalSlug) {
      return getLeagueTeamPath(urlSlug, team.canonicalSlug);
    }

    if (team.team === 'San Francisco 49ers') {
      return getLeagueTeamPath(urlSlug, 'san-francisco-49ers');
    }

    if (team.team === 'Philadelphia 76ers') {
      return getLeagueTeamPath(urlSlug, 'philadelphia-76ers');
    }

    if (urlSlug === 'mlb' && team.team === 'Oakland Athletics') {
      return getLeagueTeamPath(urlSlug, 'athletics');
    }

    return getLeagueTeamPath(
      urlSlug,
      team.canonicalSlug ??
        team.slug ??
        kebabCase(team.team.replace(/&/g, 'and'))
    );
  };

  const getTeamDisplayName = (team: { team: string }) => {
    if (leagueSlug?.toLowerCase() !== 'mls') {
      return team.team;
    }

    const canonicalSlug = getCanonicalLeagueTeamSlug(
      leagueSlug.toLowerCase(),
      kebabCase(team.team)
    );

    return getMlsTeamListName(canonicalSlug) ?? team.team;
  };

  // Filter out current team if on a team page
  const filteredTeams = currentTeamSlug
    ? flatTeams.filter((team) => {
        const teamPath = getTeamPath(team);
        return teamPath && !teamPath.endsWith(`/${currentTeamSlug}`);
      })
    : flatTeams;
  const sortedTeams = [...filteredTeams].sort((left, right) =>
    collator.compare(getTeamDisplayName(left), getTeamDisplayName(right))
  );

  const isTeamPage = !!currentTeamSlug;
  const listHeading =
    sectionTitle ||
    (isTeamPage ? `Related ${leagueName} Teams` : `All ${leagueName} Teams`);

  const teamListSchema = filteredTeams.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: listHeading,
        description: `${listHeading} with ticket and travel options.`,
        numberOfItems: filteredTeams.length,
        itemListElement: sortedTeams.map((team, index) => {
          const teamPath = getTeamPath(team);

          return {
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'SportsTeam',
              name: getTeamDisplayName(team),
              ...(teamPath && {
                url: getCanonicalUrl(teamPath),
              }),
            },
          };
        }),
      }
    : null;

  return (
    <div>
      {isProductionSeoEnabled() && teamListSchema && (
        <JsonLd id="league-team-list-schema" data={teamListSchema} />
      )}
      <h2 className="mb-8 text-2xl font-bold text-gray-800">{listHeading}</h2>
      {sectionIntro ? (
        <p className="mb-8 text-sm font-medium text-gray-500 md:text-base">
          {sectionIntro}
        </p>
      ) : null}
      {layout === 'flat' ? (
        <ul
          className={
            teamsGridClassName ||
            'grid w-full grid-cols-2 gap-x-8 gap-y-3 lg:grid-cols-4'
          }
        >
          {sortedTeams.map((team: any) => {
            const isFavorite = favoritesList?.find(
              (favorite) => favorite.id === team.teamId
            );

            return (
              <TeamItem
                key={team.teamId ?? team.team}
                displayName={getTeamDisplayName(team)}
                isFavorite={!!isFavorite}
                team={team}
                leagueSlug={leagueSlug}
                userToken={userToken}
                onFavoriteUpdate={onFavoriteUpdate}
                onRemoveFavoriteUpdate={onRemoveFavoriteUpdate}
              />
            );
          })}
        </ul>
      ) : (
        <div
          className={
            teamsGridClassName ||
            'grid-flow-rows grid w-full auto-rows-max grid-cols-2 gap-6 lg:grid-cols-6'
          }
        >
          {displayTeams.map((item) => {
            // Filter out current team from each division
            const divisionTeams = currentTeamSlug
              ? item.teams.filter((team: any) => {
                  const teamPath = getTeamPath(team);
                  return teamPath && !teamPath.endsWith(`/${currentTeamSlug}`);
                })
              : item.teams;

            // Skip empty divisions
            if (divisionTeams.length === 0) return null;

            return (
              <div
                key={`${item.division || 'group'}-${item.teams?.[0]?.teamId || item.teams?.[0]?.team || 'empty'}`}
                className="w-full"
              >
                {item.division ? (
                  <h4 className="mb-2 text-sm text-gray-500">
                    {item.division}
                  </h4>
                ) : null}
                <ul>
                  {divisionTeams.map((team: any) => {
                    const isFavorite = favoritesList?.find(
                      (favorite) => favorite.id === team.teamId
                    );
                    return (
                      <TeamItem
                        key={team.teamId ?? team.team}
                        displayName={getTeamDisplayName(team)}
                        isFavorite={!!isFavorite}
                        team={team}
                        leagueSlug={leagueSlug}
                        userToken={userToken}
                        onFavoriteUpdate={onFavoriteUpdate}
                        onRemoveFavoriteUpdate={onRemoveFavoriteUpdate}
                      />
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
